import { AccessTime } from "@mui/icons-material";
import ComputerIcon from "@mui/icons-material/Computer";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import { Box, Button, CircularProgress, Divider, Stack } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { DisagreementIcon } from "assets/icons";
import { labelInternalToUI, LabelMapping, labelMappingsIQ, sourceMappingsIQ } from "config/LabelMapping";
import { useImageSettingsContext } from "context/ImageSettingsContext/imageSettingsContext";
import { getFrontendLabelValueFromBackendLabel } from "features/queryDetailsModal/utils";
import { useAppDispatch } from "hooks/store";
import { Typography } from "new-components/typography/Typography";
import { setUnsavedAnnotations } from "store/slices/FocusModal/focusModalSlice";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { ANSWER_VALUES, DisplayBBoxInfo, ImageQuery, SOURCE_VALUES } from "types/imageQueries";
import { LabelValue } from "types/labelValue";
import { isImageQueryInQueue } from "utils/imageQueriesUtils";
import { convertUTCtoLocalReadableDate } from "utils/utilityFunctions";

import { RelabelSectionQueryDetails } from "./components/RelabelSectionQueryDetails";
import { RelabelSectionReviewPage } from "./components/RelabelSectionReviewPage";

const HIDDEN_CONFIDENCE_MODES = [DetectorModes.COUNT, DetectorModes.TEXT_RECOGNITION];

export interface AnswerPanelProps {
  loading: boolean;
  selectedImageQuery: ImageQuery;
  detector: Detector;
  onSendLabel?: () => Promise<void>;
  labelRows?: LabelValue[];
  reviewMode?: boolean;
  selectedLabelValue?: LabelValue | null;
  reviewBBoxInfo?: DisplayBBoxInfo;
}

function labeledByIcon(imageQuery: ImageQuery, labelRows?: LabelValue[], selectedLabelValue?: LabelValue | null) {
  if (!imageQuery) {
    return <AccessTime />;
  }

  if (imageQuery.in_queue && !imageQuery.human_labeled) {
    // Needs review
    return <AccessTime />;
  }

  if (!labelRows?.length) return <AccessTime />;

  switch (selectedLabelValue?.source) {
    case SOURCE_VALUES.ALGORITHM:
      if (selectedLabelValue.answer === ANSWER_VALUES.UNCLEAR) return <DisagreementIcon />;
      return <ComputerIcon />;
    default:
      return <PersonIcon />;
  }
}

export function AnswerPanelNew(props: AnswerPanelProps) {
  const {
    selectedImageQuery,
    loading,
    onSendLabel,
    detector,
    labelRows,
    reviewMode = false,
    selectedLabelValue,
    reviewBBoxInfo,
  } = props;

  const { isEditing, setIsEditing } = useImageSettingsContext();
  const { mode, mode_configuration: modeConfig } = detector;
  const [hoverAnswer, setHoverAnswer] = useState(false);
  const [className, setClassName] = useState<string | undefined>(undefined);
  const dispatch = useAppDispatch();

  const handleClose = useCallback(() => {
    setIsEditing(false);
    dispatch(setUnsavedAnnotations(false));
  }, [dispatch, setIsEditing]);

  const onRelabel = async () => {
    handleClose();
    await onSendLabel?.();
  };

  useEffect(() => {
    if (isImageQueryInQueue(selectedImageQuery)) {
      setClassName("bg-[#919599] text-white font-semibold");
    } else {
      const bestAnswerStr = selectedLabelValue ? getFrontendLabelValueFromBackendLabel(selectedLabelValue, mode) : null;

      let labelMapping: LabelMapping;

      const defaultAnswerClassName: LabelMapping = {
        backendLabel: bestAnswerStr || "",
        renderedLabel: bestAnswerStr || "",
        bannerClassName: "bg-opacity-20 bg-primary-light border border-primary",
        baseClass: "text-white font-semibold hover:text-white py-2 px-4 border hover:border-transparent shadow-xl",
        baseColor: "bg-primary-light",
        borderColor: "border-transparent",
        hoverColor: "transition ease-in-out duration-200 hover:bg-primary shadow-xl",
        renderButton: true,
        textColor: "text-white",
      };

      const answerOnLabelMappingIQ = labelMappingsIQ.filter((item) => item?.backendLabel === bestAnswerStr)[0];

      if (mode === DetectorModes.MULTICHOICE) {
        labelMapping = answerOnLabelMappingIQ || defaultAnswerClassName;
      } else if (mode === DetectorModes.COUNT) {
        if (bestAnswerStr === "0" || bestAnswerStr === `>${modeConfig?.max_count}`) {
          const defaultCountBoundsClassName: LabelMapping = {
            backendLabel: bestAnswerStr || "",
            renderedLabel: bestAnswerStr || "",
            bannerClassName: "bg-opacity-20 bg-primary-dark border border-primary",
            baseClass: "text-white font-semibold hover:text-white py-2 px-4 border hover:border-transparent shadow-xl",
            baseColor: "bg-primary-dark",
            borderColor: "border-primary-dark",
            hoverColor: "transition ease-in-out duration-200 hover:bg-primary shadow-xl",
            renderButton: true,
            textColor: "text-primary",
          };
          labelMapping = answerOnLabelMappingIQ || defaultCountBoundsClassName;
        } else {
          labelMapping = answerOnLabelMappingIQ || defaultAnswerClassName;
        }
      } else if (mode === DetectorModes.TEXT_RECOGNITION) {
        labelMapping = answerOnLabelMappingIQ || defaultAnswerClassName;
      } else {
        labelMapping = answerOnLabelMappingIQ || labelMappingsIQ[0];
      }
      setClassName(
        `border-solid hover:${labelMapping.hoverColor} ${labelMapping.baseClass} ${labelMapping.baseColor} ${labelMapping.borderColor}`
      );
    }
  }, [selectedImageQuery, selectedLabelValue, mode, modeConfig?.max_count, handleClose]);

  const isInQueue = isImageQueryInQueue(selectedImageQuery);

  const getCurrentBestAnswerText = (): string => {
    let className = "N/A";
    if (isInQueue) {
      className = "Awaiting";
    } else if (selectedLabelValue) {
      const value = getFrontendLabelValueFromBackendLabel(selectedLabelValue, mode);
      className = labelInternalToUI(labelMappingsIQ, value);
    } else if (selectedImageQuery) {
      const value = selectedImageQuery.current_best_answer_str || selectedImageQuery.current_best_prediction_str;
      if (value) {
        className = labelInternalToUI(labelMappingsIQ, value);
      }
    }
    if (mode === DetectorModes.TEXT_RECOGNITION && !className) {
      className = "No Text";
    }
    // Adding a possible suffix to the class name
    if (mode === DetectorModes.COUNT && className !== "Unclear") {
      className = `${className} Object${className === "1" ? "" : "s"}`;
    }
    return className;
  };

  const getCurrentBestAnswerConfidence = () => selectedLabelValue?.confidence?.toFixed(4) || "N/A";
  const getSource = () => labelInternalToUI(sourceMappingsIQ, selectedLabelValue?.source) || "N/A";

  const currentBestAnswerText = getCurrentBestAnswerText();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const bannerInfoContent = () => (
    <Box
      id="answer-panel-info-content-container"
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        width: "100%",
        px: { xs: "16px", md: "43px" },
        py: { xs: "16px", md: "33px" },
        gap: { xs: "16px", md: "20px" },
        overflow: "auto",
        alignItems: "center",
      }}
    >
      {loading && className && selectedImageQuery ? (
        <CircularProgress size={22} sx={{ color: "white" }} />
      ) : (
        <Button
          id="answer-panel-info-button"
          onClick={() => setIsEditing(true)}
          ref={buttonRef}
          variant="contained"
          className={className}
          sx={{
            width: "150px",
            height: "34px",
            flexShrink: 0,
            justifyItems: "center",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            borderRadius: "17px",
            "& .MuiButton-endIcon": {
              color: "#fff",
            },
            "&:hover": {
              opacity: 0.8,
            },
          }}
          onMouseEnter={() => setHoverAnswer(true)}
          onMouseLeave={() => setHoverAnswer(false)}
          endIcon={
            <Box className="items-center opacity-60 h-full hidden md:flex gap-1">
              {hoverAnswer && !isInQueue ? (
                <EditIcon />
              ) : (
                labeledByIcon(selectedImageQuery, labelRows, selectedLabelValue) ||
                labelInternalToUI(sourceMappingsIQ, selectedLabelValue?.source)
              )}
            </Box>
          }>
          <Typography variant="detail" fontWeight="bold" noWrap>
            {currentBestAnswerText}
          </Typography>
        </Button>
      )}

      <Divider orientation="vertical" variant="middle" flexItem />

      <Stack
        id="answer-panel-info-content"
        sx={{
          minHeight: "34px",
          width: "100%",
          gap: { xs: 1, md: 0 },
        }}
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={1}>
          <Typography id="answer-panel-info-image-query-id" variant="small">
            <span className="text-[#529FE8] font-bold">Image Query ID:</span> {`${selectedImageQuery?.iq_id}`}
          </Typography>
          <Typography id="answer-panel-info-source" variant="small">
            <span className="text-[#529FE8] font-bold">Source:</span> {getSource()}
          </Typography>
        </Stack>

        <Stack direction="column" spacing={1}>
          <Typography id="answer-panel-info-created-by" variant="small">
            <span className="text-[#529FE8] font-bold">Created at:</span>{" "}
            {convertUTCtoLocalReadableDate(selectedImageQuery?.created_at)}
          </Typography>
          {!HIDDEN_CONFIDENCE_MODES.includes(detector.mode) && (
            <Typography id="answer-panel-info-confidence" variant="small">
              <span className="text-[#529FE8] font-bold">Confidence:</span> {getCurrentBestAnswerConfidence()}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );

  const coreRelabeler = () =>
    reviewMode ? (
      <RelabelSectionReviewPage
        posicheckId={selectedImageQuery?.iq_id}
        onClose={handleClose}
        lastLabel={selectedImageQuery?.current_best_answer_str}
        mode={mode}
        modeConfig={modeConfig}
        reviewBBoxInfo={reviewBBoxInfo}
      />
    ) : (
      <RelabelSectionQueryDetails
        posicheckId={selectedImageQuery?.iq_id}
        onSubmit={onRelabel}
        onClose={handleClose}
        selectedLabelValue={selectedLabelValue}
        mode={mode}
        modeConfig={modeConfig}
        reviewBBoxInfo={reviewBBoxInfo}
      />
    );

  return (
    <Box
      id="answer-panel"
      width="100%"
      className="bg-[#E5E9ED]"
      sx={{
        borderRadius: "0 0 10px 10px",
        boxShadow: "0px -4px 6px -1px rgba(0, 0, 0, 0.1), 0px -2px 4px -2px rgba(0, 0, 0, 0.1)",
      }}
    >
      {(isEditing || reviewMode) && selectedImageQuery ? coreRelabeler() : bannerInfoContent()}
    </Box>
  );
}
